import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { API, ConnexDesignSystem, Core, Localization } from 'connex-cds';
import { BrowserRouter } from 'react-router-dom';
import styled from 'styled-components';
import AppRoutes from './AppRoutes';
import { mergeMessages } from './i18n/entitySpecific';
import { useLazyLocalMessages } from './i18n/useLazyLocalMessages';
import { getFeatureFlaggedMenuConfig } from './views/apps/entity-ref/menu-config';
import FeatureFlagsProvider, { useFeatureFlags } from './providers/feature-flags-provider';
import { useEntityTranslations } from './views/apps/entity-ref/mobile-ticket/MasterDataProvider';

const Styled = styled.div`
  height: 100vh;
`;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        return failureCount < 3 && error.status >= 500;
      },
    },
  },
});

const LocaleLoader = ({ onLocaleChange }) => {
  const { userLocale } = Localization?.useLocalizationContext?.() ?? {};

  React.useEffect(() => {
    if (!userLocale) {
      return;
    }

    Promise.resolve(onLocaleChange?.(userLocale))?.catch?.(() => {});
  }, [onLocaleChange, userLocale]);

  return null;
};

const FeatureFlaggedDesignSystem = ({ children, messages }) => {
  const { getFlagValue } = useFeatureFlags() ?? {};
  const featureFlaggedMenuConfig = React.useMemo(
    () => getFeatureFlaggedMenuConfig(getFlagValue),
    [getFlagValue]
  );

  return (
    <ConnexDesignSystem
      messages={messages}
      appId="mt"
      breakpoints={[950]}
      analytics={false}
      menuConfig={featureFlaggedMenuConfig}
      contentOnly={true}
      queryClient={queryClient}
      initialTimeZone="America/Chicago"
    >
      {children}
    </ConnexDesignSystem>
  );
};

const App = () => {
  const [isConfiguring, setIsConfiguring] = React.useState(true);
  const { data: entityMessages, setEntityRef, loading: fetchingEntityMessages } = useEntityTranslations();
  const { loadedLocalMessages, handleLocaleChange, isLocaleReady, requestedLocale } = useLazyLocalMessages();

  const handleMessageEvent = React.useCallback(
    event => {
      const type = event?.data?.type;
      if (['entity-context', 'req-entity-context'].includes(type)) {
        setEntityRef(event?.data?.token?.entityRef);
      }
    },
    [setEntityRef]
  );

  React.useEffect(() => {
    window.addEventListener('message', handleMessageEvent);
    return () => {
      window.removeEventListener('message', handleMessageEvent);
    };
  }, [handleMessageEvent]);

  React.useEffect(() => {
    const [entityRef] = window?.location?.pathname?.split('/').filter(Boolean);
    if (entityRef) {
      setEntityRef(entityRef);
    }

    API.initialize().finally(() => {
      setIsConfiguring(false);
    });
  }, []);

  // Routes stay mounted once rendered so a later locale switch shows the spinner instead of unmounting in-progress work.
  const hasRenderedRoutes = React.useRef(false);

  if (isLocaleReady) {
    hasRenderedRoutes.current = true;
  }

  const localMessagesWithFallback = React.useMemo(() => {
    if (!requestedLocale || loadedLocalMessages?.[requestedLocale]) {
      return loadedLocalMessages;
    }

    return { ...loadedLocalMessages, [requestedLocale]: loadedLocalMessages?.['en-US'] };
  }, [loadedLocalMessages, requestedLocale]);

  const messages = React.useMemo(() => {
    if (!fetchingEntityMessages) {
      return mergeMessages({ entityMessages, localMessages: localMessagesWithFallback });
    }

    return localMessagesWithFallback;
  }, [entityMessages, fetchingEntityMessages, localMessagesWithFallback]);

  return (
    <QueryClientProvider client={queryClient}>
      <Core.Spinner spin={isConfiguring || fetchingEntityMessages}>
        <BrowserRouter>
          <FeatureFlagsProvider enabled={!isConfiguring}>
            <FeatureFlaggedDesignSystem messages={messages}>
              <LocaleLoader onLocaleChange={handleLocaleChange} />
              <Styled>
                <Core.Spinner spin={!isLocaleReady}>
                  {isLocaleReady || hasRenderedRoutes?.current ? <AppRoutes /> : null}
                </Core.Spinner>
              </Styled>
            </FeatureFlaggedDesignSystem>
          </FeatureFlagsProvider>
        </BrowserRouter>
      </Core.Spinner>
    </QueryClientProvider>
  );
};

export default App;
